<template>
    <TprmModal
        additional-class-name="AddIndividualEntityModal"
        size="lg"
        open
        title="Add Entity"
        @close="close"
    >
        <form @submit.prevent="createEntity">
            <Alert v-if="alertConfig" :type="alertConfig.type">
                <template #title>
                    <FontAwesomeIcon :icon="faExclamationTriangle" />
                    {{ alertConfig ? alertConfig.title : null }}
                </template>
            </Alert>
            <div v-if="form" class="AddIndividualEntityModal__inputSection">
                <JsonSchemaForm
                    ref="formElement"
                    v-model="value"
                    :form="form"
                    :disabled="saving"
                    :custom-component-input="getCustomComponent"
                    @submit="createEntity"
                />
            </div>
            <MigratedComplianceSummary
                class="AddIndividualEntityModal__migratedSummary"
                :items="migratedRequirements"
                :expired="expiredRequirements"
            />
        </form>

        <template #footer>
            <NewButton
                color="primary"
                class="AddIndividualEntityModal__submitButton"
                :disabled="!valid"
                :loading="saving"
                @click="createEntity"
            >
                Save entity
            </NewButton>
        </template>
    </TprmModal>
</template>

<script lang="ts">
    import { Component, Prop, Vue, Watch, Ref } from '@evidentid/vue-property-decorator';

    import omit from 'lodash/omit';

    import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import { Alert } from '@evidentid/dashboard-commons/components/Alert';
    import { AlertType } from '@evidentid/dashboard-commons/components/Alert/types';
    import { FormElement, FormInput } from '@evidentid/dashboard-commons/components/Form';
    import { JsonSchemaForm } from '@evidentid/dashboard-commons/components/JsonSchemaForm';
    import JsonSchemaAddressForm
        from '@evidentid/dashboard-commons/components/JsonSchemaForm/customElements/JsonSchemaAddressForm.vue';
    import JsonSchemaCountryEnumForm
        from '@evidentid/dashboard-commons/components/JsonSchemaForm/customElements/JsonSchemaCountryEnumForm.vue';
    import JsonSchemaConstForm
        from '@evidentid/dashboard-commons/components/JsonSchemaForm/elements/JsonSchemaConstForm.vue';
    import createForm from '@evidentid/json-schema/createForm';
    import { JsonFormType } from '@evidentid/json-schema/interfaces/JsonForm';
    import { TprmSchema } from '@evidentid/tprm-portal-lib/models/common/TprmSchema.model';
    import {
        TprmSchemaDisplayFormat,
    } from '@evidentid/tprm-portal-lib/models/common/TprmSchemaDisplayFormat.model';
    import { CustomProperty, EntityInput } from '@evidentid/tprm-portal-lib/models/dashboard';

    import LoadingModal from '@/components/loading-modal/LoadingModal.vue';
    import {
        getExpiredRequirements,
        getMigratedRequirements,
        MigratedRequirement,
        RequirementLabels,
    } from '@/components/migrated-compliance-section/migratedCompliance';
    import MigratedComplianceSummary from '@/components/migrated-compliance-section/MigratedComplianceSummary.vue';
    import NewButton from '@/components/new-button/NewButton.vue';
    import TprmModal from '@/components/tprm-modal/TprmModal.vue';
    import CriterionEnumInput
        from '@/modules/decisioning-criteria/components/CriterionInputComponents/CriterionEnumInput.vue';
    import { removeNonStandardSchemaFormat } from '@/modules/decisioning-criteria/utils/removeNonStandardSchemaFormat';
    import JsonSchemaMigratedComplianceForm
        from '@/modules/entity-management/components/JsonSchemaMigratedComplianceForm/JsonSchemaMigratedComplianceForm.vue';
    import {
        buildSingleEntityImportJsonSchema,
    } from '@/modules/entity-management/utils/build-entity-json-schema/buildEntityJsonSchema';
    import {
        applyMigratedComplianceSchema,
        MIGRATED_COMPLIANCE_DISPLAY_FORMAT,
    } from '@/modules/entity-management/utils/build-entity-json-schema/migratedComplianceSchema';
    import {
        filterEntityEmptyItems,
        hasPastExpirationDate,
        trimEntity,
    } from '@/modules/entity-management/utils/entityProcessing';
    import { getJsonSchemaCompFunction } from '@/utils/getJsonSchemaCustomCompFuntion';

    interface EntityFormInput extends Omit<EntityInput, 'displayName' | 'legalName' | 'doingBusinessAs'> {
        insuredName: {
            displayName: string;
            legalName?: string | null;
            doingBusinessAs?: string[] | null;
        };
    }

    // PRODUCT-26110 QA fixes4: declares every event this component `$emit`s -- without a
    // declared `emits`, Vue 3 warns "Component emitted event 'close'/'submit' but it is
    // neither declared in the emits option nor as an 'onClose'/'onSubmit' prop" every
    // time it fires.
    @Component({
        emits: [ 'close', 'submit' ],
        components: {
            Alert,
            NewButton,
            TprmModal,
            FormElement,
            FormInput,
            FontAwesomeIcon,
            JsonSchemaForm,
            LoadingModal,
            MigratedComplianceSummary,
        },
    })
    export default class AddIndividualEntityModal extends Vue {
        @Prop({ type: Array, default: () => [] })
        private customProperties!: CustomProperty[];

        @Prop({ type: Array, default: () => [] })
        private requirementTypes!: string[];

        @Prop({ type: Object, default: () => ({}) })
        private requirementDefinitions!: RequirementLabels;

        @Prop({ type: Object, default: null })
        private alertConfig!: { type: AlertType, title: string } | null;

        @Prop({ type: Boolean, default: false })
        private saving!: boolean;

        @Ref()
        private formElement!: JsonSchemaForm;

        private faExclamationTriangle = faExclamationTriangle;

        private form = null as any;
        private value: EntityFormInput = null as any;

        // TODO: needs to rename CriterionEnumInput more generic as UX prefer it for most of dropdown input
        private customComponents = {
            [JsonFormType.enum]: CriterionEnumInput,
            [TprmSchemaDisplayFormat.hidden]: JsonSchemaConstForm,
            [TprmSchemaDisplayFormat.address]: JsonSchemaAddressForm,
            [TprmSchemaDisplayFormat.country]: JsonSchemaCountryEnumForm,
            [MIGRATED_COMPLIANCE_DISPLAY_FORMAT]: JsonSchemaMigratedComplianceForm,
        };
        private getCustomComponent = getJsonSchemaCompFunction(this.customComponents);

        private get migratedRequirements(): MigratedRequirement[] {
            return getMigratedRequirements(
                this.value?.exceptions, [ ...this.requirementTypes ].sort(), this.requirementDefinitions,
            );
        }

        private get expiredRequirements(): MigratedRequirement[] {
            return getExpiredRequirements(
                this.value?.exceptions, [ ...this.requirementTypes ].sort(), this.requirementDefinitions,
            );
        }

        private get valid(): boolean {
            return this.form?.isValid(filterEntityEmptyItems(this.value, this.customProperties), true);
        }

        @Watch('customProperties', { immediate: true })
        private onCustomPropertiesUpdate(): void {
            const sortedRequirementTypes = [ ...this.requirementTypes ].sort();
            this.form = createForm(removeNonStandardSchemaFormat(applyMigratedComplianceSchema(
                buildSingleEntityImportJsonSchema(this.customProperties, sortedRequirementTypes),
                this.requirementDefinitions,
            )));
            this.value = this.form.getValue(undefined, true);
            this.setAddressCustomPropertiesDefaultCountryValue();
        }

        private close(): void {
            this.$emit('close');
        }

        private convertFormInputToEntityInput(entity: EntityFormInput): EntityInput {
            // flatten insuredName object that was used for view only
            return {
                ...entity.insuredName,
                ...omit(entity, 'insuredName'),
            };
        }

        private createEntity(): void {
            if (this.valid) {
                const trimmedAndFilteredFormEntity =
                    filterEntityEmptyItems(trimEntity(this.value), this.customProperties);
                const entity =
                    this.convertFormInputToEntityInput(this.form.getValue(trimmedAndFilteredFormEntity, true));
                this.$emit('submit', entity, hasPastExpirationDate(this.value));
            } else {
                // Do it after the form renders errors
                this.formElement.scrollToFirstError(true);
            }
        }

        private setAddressCustomPropertiesDefaultCountryValue(): void {
            if (this.value.insuredFields) {
                const customPropertiesValue = this.value.insuredFields;
                const isAddress = (field: CustomProperty) =>
                    (field.schema as TprmSchema).displayFormat === TprmSchemaDisplayFormat.address;
                const addressFieldKeys = this.customProperties.filter(isAddress).map((field) => field.key);
                addressFieldKeys.forEach((key: string) => {
                    const field = customPropertiesValue[key];
                    if (field && typeof field === 'object' && 'country' in field && !field.country) {
                        field.country = 'US';
                    }
                });
            }
        }
    }
</script>
